import React from "react";
import { StyleSheet, Switch, Text as RNText, TextInput } from "react-native";
import { useTheme } from "@react-navigation/native";
import * as WebBrowser from "expo-web-browser";
import { Text, View } from "@components/atoms/Themed";
import TtgoStatusBadge, { TtgoStatus } from "@components/atoms/TtgoStatusBadge";
import Spacer from "@components/atoms/Spacer";
import Icon from "@components/atoms/Icon";
import OuterLabel from "@components/molecules/TextInput/OuterLabel";
import inputStyles from "@components/molecules/TextInput/style";
import openContact from "@lib/util/openContact";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import { TtgoApplicationFormUrl } from "@constants/App";

type Props = {
  /** サーバー上の is_tiktok_go_creator */
  isTiktokGoCreator: boolean;
  /** サーバー上の tiktok_go_status */
  tiktokGoStatus: TtgoStatus;
  /** ローカル編集中の "申請するか" の値（controlled） */
  apply: boolean;
  /** ローカル編集中のユニークID（controlled） */
  uniqueId: string;
  /** トグル切り替え時 */
  onChangeApply: (next: boolean) => void;
  /** ユニークID入力変更時 */
  onChangeUniqueId: (next: string) => void;
};

export default function TtgoCreatorSwitch({
  isTiktokGoCreator,
  tiktokGoStatus,
  apply,
  uniqueId,
  onChangeApply,
  onChangeUniqueId,
}: Props) {
  const { colors, dark } = useTheme();

  // サーバ初期状態で「申請中扱い」になっているかどうか（バッジ表示の判定に使う）。
  // 却下中は DB上 is_creator=true のままだが、サーバ初期状態としては OFF 表示とする。
  const initialDisplayIsCreator =
    isTiktokGoCreator && tiktokGoStatus !== "rejected";
  const isRejectedAndDisplayedOff =
    isTiktokGoCreator && tiktokGoStatus === "rejected";

  return (
    <View>
      <View style={styles.row}>
        <View style={styles.left}>
          <Text style={Fonts.lb130}>TikTokGOクリエイター</Text>
          <Spacer height={4} />
          <Text style={styles.desc}>
            承認されると、TikTokGO案件に応募できるようになります
          </Text>
          {initialDisplayIsCreator && (
            <>
              <Spacer height={6} />
              <TtgoStatusBadge status={tiktokGoStatus} />
            </>
          )}
        </View>
        <Switch onValueChange={onChangeApply} value={apply} />
      </View>

      {isRejectedAndDisplayedOff && !apply && (
        <>
          <Spacer height={12} />
          <View style={styles.rejectedNotice}>
            <Text style={styles.rejectedTitle}>前回の申請は却下されました</Text>
            <Spacer height={4} />
            <Text style={styles.rejectedDesc}>
              再度申請する場合は上のスイッチをONにし、画面下の「保存する」を押してください。
            </Text>
          </View>
        </>
      )}

      {apply && (
        <>
          <Spacer height={16} />
          <OuterLabel label="TikTokGO ユニークID" required={false} />
          <Spacer height={8} />
          <TextInput
            autoCapitalize="none"
            autoCorrect={false}
            onChangeText={onChangeUniqueId}
            placeholder="例: stormpyui"
            placeholderTextColor={Colors.gray}
            selectionColor={colors.text}
            style={[
              inputStyles.form,
              {
                color: colors.text,
                backgroundColor: dark ? colors.background : colors.card,
              },
            ]}
            value={uniqueId}
          />
          <Spacer height={8} />
          <Text style={styles.desc}>
            ベクトルのTikTok
            GOエージェンシーチームとクリエイター連携されていない方は、ユニークIDの記載と併せて
            <InlineLink
              onPress={openTtgoApplicationForm}
              title="お問い合わせフォームから申請をお願いします"
            />
          </Text>
          <Spacer height={4} />
          <Text style={styles.desc}>
            ユニークID変更時は、
            <InlineLink onPress={openContact} title="運営までご一報ください" />
          </Text>
        </>
      )}
    </View>
  );
}

function openTtgoApplicationForm() {
  WebBrowser.openBrowserAsync(TtgoApplicationFormUrl);
}

// 文中に埋め込むリンク。ワークチャットの「運営への問い合わせはこちら」と同じ見た目にする。
// Themed の Text はダークモードで文字色を白に上書きしリンクと見分けられなくなるため、
// 素の Text とアイコンの skipDarkModeColor でダークモードでも青を保つ。
function InlineLink({
  title,
  onPress,
}: {
  title: string;
  onPress: () => void;
}) {
  return (
    <RNText onPress={onPress} style={styles.link}>
      <Icon color={Colors.blue} name="outer" size={14} skipDarkModeColor />{" "}
      {title}
    </RNText>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  left: { flex: 1, paddingRight: 12 },
  desc: {
    ...Fonts.lr130,
    color: Colors.gray,
  },
  link: {
    color: Colors.blue,
  },
  rejectedNotice: {
    padding: 12,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: Colors.orange,
  },
  rejectedTitle: {
    ...Fonts.lb130,
    color: Colors.orange,
  },
  rejectedDesc: {
    ...Fonts.lr130,
    color: Colors.orange,
  },
});
